import { type EligibilityConditions } from "src/generated/hooks/form-service/requests";
import { z } from "zod";

type Comparison = NonNullable<
  EligibilityConditions["expression"]
>["anyOf"][number]["allOf"][number];
type MembershipComparison = Extract<Comparison, { kind: "Membership" }>;

export type AnswerSelectionOperator = "in" | "notIn";

export type AnswerOption = {
  id: string;
  value: string;
};

export type AnswerSelectionCondition = {
  kind: "answerSelection";
  id: string;
  dataPointId: string;
  operator: AnswerSelectionOperator | "";
  answerOptions: AnswerOption[];
};

export function emptyAnswerOption(): AnswerOption {
  return { id: crypto.randomUUID(), value: "" };
}

export function emptyAnswerSelection(): AnswerSelectionCondition {
  return {
    kind: "answerSelection",
    id: crypto.randomUUID(),
    dataPointId: "",
    operator: "",
    answerOptions: [emptyAnswerOption()],
  };
}

const requiredMessage = "This field is required";

const answerOptionSchema = z.object({
  id: z.string(),
  value: z.string().trim().min(1, requiredMessage),
});

export const answerSelectionSchema = z.object({
  kind: z.literal("answerSelection"),
  id: z.string(),
  dataPointId: z.string().min(1, requiredMessage),
  operator: z.string().min(1, requiredMessage),
  answerOptions: z
    .array(answerOptionSchema)
    .min(1, "At least one option value is required"),
});

function readAnswerSelectionOperator(
  operator: string
): AnswerSelectionOperator | null {
  if (operator === "In") return "in";
  if (operator === "NotIn") return "notIn";
  return null;
}

export function comparisonToAnswerSelection(
  comparison: Comparison
): AnswerSelectionCondition | null {
  if (comparison.kind !== "Membership") return null;
  const operator = readAnswerSelectionOperator(comparison.operator);
  if (!operator) return null;

  const answerOptions: AnswerOption[] = [];
  for (const option of comparison.options) {
    if (option.value == null || option.value.trim() === "") return null;
    answerOptions.push({ id: crypto.randomUUID(), value: option.value });
  }
  if (answerOptions.length === 0) return null;

  return {
    kind: "answerSelection",
    id: crypto.randomUUID(),
    dataPointId: comparison.value.id,
    operator,
    answerOptions: answerOptions,
  };
}

export function answerSelectionToComparison(
  condition: AnswerSelectionCondition
): MembershipComparison {
  const answerOptionValues = condition.answerOptions.map((option) =>
    option.value.trim()
  );
  if (
    (condition.operator !== "in" && condition.operator !== "notIn") ||
    condition.dataPointId === "" ||
    answerOptionValues.length === 0 ||
    answerOptionValues.some((value) => value === "")
  ) {
    throw new Error("Answer selection is incomplete");
  }

  return {
    kind: "Membership",
    operator: condition.operator === "in" ? "In" : "NotIn",
    value: {
      source: "Reference",
      id: condition.dataPointId,
      type: "SingleSelect",
    },
    options: answerOptionValues.map((value) => ({ source: "Literal", value })),
  };
}

export function describeAnswerSelection(
  condition: AnswerSelectionCondition,
  dataPoints: Map<string, string> // id -> name
): string {
  const label = dataPoints.get(condition.dataPointId) ?? "Unknown data point";
  if (
    (condition.operator === "in" || condition.operator === "notIn") &&
    condition.answerOptions.length > 0
  ) {
    const relation = condition.operator === "in" ? "is" : "is not";
    const answers =
      condition.answerOptions.length === 1
        ? condition.answerOptions[0]?.value
        : `any of: ${condition.answerOptions
            .map((option) => option.value)
            .join(", ")}`;
    return `${label} ${relation} ${answers}`;
  }
  return label;
}
